import { alpha, createTheme } from '@mui/material/styles';
import type { } from '@mui/lab/themeAugmentation';
import { Handshake, LocalShipping } from '@mui/icons-material';
import { ReactComponent as AllabolagIcon } from '@icons/allabolag-icon.svg';
import { ReactComponent as FairTransportIcon } from '@icons/fairtransport-icon.svg';

// ---------------------------------------------------------------------------
// Brand — grafisk profil v1.0
// ---------------------------------------------------------------------------

export const COLOR_APP_PRIMARY = '#1D2570'; // indigo
export const COLOR_APP_SECONDARY = '#7DCFA5'; // mint
export const COLOR_BLUE = '#0E2A43'; // navy
export const COLOR_TEAL = '#006D77'; // petrol
export const COLOR_TEAL_SURFACE = '#E4EFF0';
export const COLOR_MINT = COLOR_APP_SECONDARY;
export const COLOR_MINT_LIGHT = '#B1E2C9'; // mint 60 %
export const COLOR_GREEN_LIGHT = COLOR_MINT_LIGHT;
export const COLOR_OUTLINE = '#6E7F8E'; // navy 60 %
export const COLOR_SLATE_LIGHT = '#9EAAB7'; // skiffer 60 %
export const COLOR_WHITEISH = '#EFF5F6'; // ljus petrol 60 %
export const COLOR_WHITE = '#FFFFFF';
export const COLOR_NAV_HOVER = 'rgba(255, 255, 255, 0.10)';
export const COLOR_NAV_ACTIVE = 'rgba(255, 255, 255, 0.18)';
export const COLOR_RED = '#FF0000';
export const COLOR_GREEN = '#2E7D32';
export const COLOR_ORANGE = '#ef6800';
export const COLOR_DHL_YELLOW = '#FDB827';

// ---------------------------------------------------------------------------
// App chrome
// ---------------------------------------------------------------------------

export const COLOR_PAGE_BACKGROUND = COLOR_TEAL_SURFACE;
export const COLOR_MENU_PAPER = '#0F1438';
export const COLOR_MENU_ACTIVE = '#171D52';
export const COLOR_PRIMARY_TEXT = COLOR_WHITE;
export const COLOR_ACTIVE_TEXT = COLOR_APP_SECONDARY;

// ---------------------------------------------------------------------------
// Chat + list
// ---------------------------------------------------------------------------

export const COLOR_OTHERS_CHAT_MESSAGE_BG = COLOR_TEAL_SURFACE;
export const COLOR_OWN_CHAT_MESSAGE_BG = '#522653';
export const COLOR_CHAT_EVENT_BG = COLOR_WHITEISH;
export const COLOR_CHAT_EVENT_TEXT = '#555555';
export const COLOR_CHAT_MESSAGE_TEXT = COLOR_WHITE;
export const COLOR_LIST_BACKGROUND = COLOR_WHITEISH;
export const COLOR_LIST_ACCORDION = COLOR_WHITEISH;

// ---------------------------------------------------------------------------
// Map
// ---------------------------------------------------------------------------

export const COLOR_MAP_BG = COLOR_WHITE;
export const COLOR_ORDER_ARROW = COLOR_TEAL;
export const COLOR_CAPACITY_ARROW = '#795679';
export const COLOR_MAP_ARROW = COLOR_ORDER_ARROW;
export const COLOR_TENANT_MAP_FILTER_MARKER_ICON_STROKE = '#B31412';
export const COLOR_TENANT_MAP_FILTER_MARKER_ICON_FILL = '#EA4335';
export const COLOR_TENANT_MAP_MARKER_LABEL = '#B31412';

export const COLOR_GREY_TRANSLUCENT = alpha(COLOR_SLATE_LIGHT, 0.1);

export const COLOR_BLUE_GRAD_MID = '#215893';
export const COLOR_BLUE_GRAD_END_HEX = '#2867AB';
export const COLOR_BLUE_GRAD_START = alpha(COLOR_BLUE, 0.9);
export const COLOR_BLUE_GRAD_MIDDLE = alpha(COLOR_BLUE_GRAD_MID, 0.9);
export const COLOR_BLUE_GRAD_END = alpha(COLOR_BLUE_GRAD_END_HEX, 0.9);
export const COLOR_BLUE_GRADIENT = `linear-gradient(135deg, ${COLOR_BLUE_GRAD_START} 0%, ${COLOR_BLUE_GRAD_MIDDLE} 65%, ${COLOR_BLUE_GRAD_END} 100%)`;

export const COLOR_GRAD_MIDDLE_HEX = '#F4EAEA';
export const COLOR_WHITE_GRAD_END_HEX = '#E9E1E1';
export const COLOR_WHITE_GRAD_START = alpha(COLOR_WHITEISH, 0.16);
export const COLOR_GRAD_MIDDLE = alpha(COLOR_GRAD_MIDDLE_HEX, 0.1);
export const COLOR_WHITE_GRAD_END = alpha(COLOR_WHITE_GRAD_END_HEX, 0.05);
export const COLOR_WHITE_GRADIENT = `linear-gradient(135deg, ${COLOR_WHITE_GRAD_START} 0%, ${COLOR_GRAD_MIDDLE} 50%, ${COLOR_WHITE_GRAD_END} 100%)`;

export const COLOR_GREEN_GRAD_START = alpha(COLOR_MINT_LIGHT, 0.1);
export const COLOR_GREEN_GRAD_MIDDLE = alpha(COLOR_MINT, 0.05);
export const COLOR_GREEN_GRAD_END = alpha(COLOR_MINT_LIGHT, 0.05);
export const COLOR_GREEN_GRADIENT = `linear-gradient(135deg, ${COLOR_GREEN_GRAD_START} 0%, ${COLOR_GREEN_GRAD_MIDDLE} 50%, ${COLOR_GREEN_GRAD_END} 100%)`;

export const colors = {
    blue: COLOR_BLUE,
    blueGradient: COLOR_BLUE_GRADIENT,
    greenLight: COLOR_GREEN_LIGHT,
    greenGradient: COLOR_GREEN_GRADIENT,
    greyTransluscent: COLOR_GREY_TRANSLUCENT,
    mint: COLOR_MINT,
    tealSurface: COLOR_TEAL_SURFACE,
    white: COLOR_WHITE,
    whiteGradient: COLOR_WHITE_GRADIENT
} as const;

// ---------------------------------------------------------------------------
// iOS slider (demo)
// ---------------------------------------------------------------------------

export const COLOR_IOS_SLIDER_DARK = '#3880FF';
export const COLOR_IOS_SLIDER_LIGHT = COLOR_IOS_SLIDER_DARK;
export const COLOR_IOS_SLIDER_BG = COLOR_WHITE;
export const COLOR_IOS_SLIDER_VALUELABEL_BG_DARK = '#000000';
export const COLOR_IOS_SLIDER_VALUELABEL_BG_LIGHT = COLOR_WHITE;
export const COLOR_IOS_SLIDER_RAIL = '#BFBFBF';
export const COLOR_IOS_SLIDER_MARK = '#BFBFBF';
export const COLOR_IOS_PRETTO_SLIDER = '#52AF77';
export const COLOR_IOS_PRETTO_SLIDER_THUMB = COLOR_WHITE;
export const COLOR_IOS_PRETTO_SLIDER_VALUELABEL_BG = '#52AF77';
export const COLOR_IOS_AIRBNB_SLIDER = '#3A8589';
export const COLOR_IOS_AIRBNB_SLIDER_THUMB = COLOR_WHITE;
export const COLOR_IOS_AIRBNB_SLIDER_RAIL_DARK = '#BFBFBF';
export const COLOR_IOS_AIRBNB_SLIDER_RAIL_LIGHT = '#D8D8D8';
export const COLOR_IOS_AIRBNB_SLIDER_BOX_SHADOW = 'rgba(58, 133, 137, 0.16)';
export const COLOR_IOS_BOX_SHADOW_1 = 'rgba(0,0,0,0.1)';
export const COLOR_IOS_BOX_SHADOW_2 = 'rgba(0,0,0,0.13)';
export const COLOR_IOS_BOX_SHADOW_3 = 'rgba(0,0,0,0.02)';
export const COLOR_IOS_SLIDER_BOX_SHADOW_1 = 'rgba(0,0,0,0.1)';
export const COLOR_IOS_SLIDER_BOX_SHADOW_2 = 'rgba(0,0,0,0.3)';
export const COLOR_IOS_SLIDER_BOX_SHADOW_3 = 'rgba(0,0,0,0.02)';

export const COLORS_CHART = [
    COLOR_SLATE_LIGHT,
    COLOR_ORDER_ARROW,
    COLOR_APP_SECONDARY,
    COLOR_OTHERS_CHAT_MESSAGE_BG
];

// ---------------------------------------------------------------------------
// Icons, type, elevation
// ---------------------------------------------------------------------------

const CARRIER_ICON = LocalShipping;
const BUYER_ICON = Handshake;
const ALLABOLAG_ICON = AllabolagIcon;
const FAIRTRANSPORT_ICON = FairTransportIcon;

export const icons = {
    carrier: CARRIER_ICON,
    buyer: BUYER_ICON,
    allabolag: ALLABOLAG_ICON,
    fairtransport: FAIRTRANSPORT_ICON
} as const;

export const FONT_FAMILY_SANS = [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Helvetica',
    'Arial',
    'sans-serif'
].join(',');

export const FONT_FAMILY_DISPLAY = [
    'Fraunces',
    'Georgia',
    'serif'
].join(',');

const MD_ELEV_1 = '0 1px 2px rgba(14,42,67,0.06), 0 1px 3px rgba(14,42,67,0.04)';
const MD_ELEV_2 = '0 2px 4px rgba(14,42,67,0.06), 0 4px 8px rgba(14,42,67,0.05)';
const MD_ELEV_3 = '0 4px 8px rgba(14,42,67,0.08), 0 8px 16px rgba(14,42,67,0.06)';

export function createAppTheme() {
    return createTheme({
        palette: {
            primary: {
                main: COLOR_APP_PRIMARY,
                contrastText: COLOR_WHITE
            },
            secondary: {
                main: COLOR_APP_SECONDARY,
                contrastText: COLOR_MENU_PAPER
            },
            background: {
                default: COLOR_PAGE_BACKGROUND,
                paper: COLOR_WHITE
            }
        },
        typography: {
            fontFamily: FONT_FAMILY_SANS,
            button: {
                fontFamily: FONT_FAMILY_SANS,
                fontWeight: 600,
                fontSize: 12,
                lineHeight: '16px',
                letterSpacing: '0.07em',
                textTransform: 'uppercase'
            }
        },
        shape: {
            borderRadius: 12
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: COLOR_PAGE_BACKGROUND
                    }
                }
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true
                },
                styleOverrides: {
                    root: {
                        borderRadius: 9999,
                        fontWeight: 600
                    },
                    sizeMedium: {
                        height: 40,
                        padding: '0 24px'
                    },
                    textSizeMedium: {
                        padding: '0 12px'
                    },
                    contained: {
                        '&:hover': {
                            boxShadow: MD_ELEV_1
                        }
                    },
                    outlined: {
                        borderColor: COLOR_OUTLINE
                    }
                }
            },
            MuiLoadingButton: {
                defaultProps: {
                    disableElevation: true
                }
            },
            MuiPaper: {
                styleOverrides: {
                    rounded: {
                        borderRadius: 12
                    },
                    elevation1: { boxShadow: MD_ELEV_1 },
                    elevation2: { boxShadow: MD_ELEV_2 },
                    elevation3: { boxShadow: MD_ELEV_2 }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 8
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        height: 32,
                        borderRadius: 8,
                        fontWeight: 500,
                        letterSpacing: '0.1px'
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderColor: 'rgba(14, 42, 67, 0.08)',
                        padding: '14px 16px'
                    },
                    head: {
                        fontWeight: 600,
                        fontSize: 13,
                        letterSpacing: '0.01em',
                        textTransform: 'none',
                        color: COLOR_BLUE,
                        backgroundColor: COLOR_WHITE
                    }
                }
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&:last-child td, &:last-child th': {
                            borderBottom: 0
                        }
                    }
                }
            },
            MuiSnackbarContent: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        boxShadow: MD_ELEV_1
                    }
                }
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 12,
                        boxShadow: MD_ELEV_3
                    }
                }
            }
        }
    });
}
